import {useCallback,useEffect,useRef,useState} from 'react';
import axiosInstance from '../utility/axiosInstance';
import type {FreeResourcesOverview} from '../types/freeResources';

/**
 * The account's included free VMs (GET /v1/free-resources/overview), for the home page card and the instances table.
 *
 * Read on mount, then only when something says it changed: the socket's account refresh (socketService turns the
 * 'update' {registration_account} event into 'registration-account-changed'; every free-VM state change publishes it),
 * the window regaining focus, or the caller's own `reload` after an action. Nothing polls this data.
 *
 * Only the newest request's answer is applied, so a late 'provisioning' cannot overwrite a newer 'active'. A failed
 * refresh keeps the last good overview (the rows stay where they were); a failed first load stays null.
 */
export default function useFreeResourcesOverview(): {overview: FreeResourcesOverview | null; reload: () => void} {
    const [overview,setOverview]=useState<FreeResourcesOverview | null>(null);
    const latest=useRef(0);
    const mounted=useRef(false);
    const reload=useCallback(()=>{
        const ticket=++latest.current;
        axiosInstance.get<FreeResourcesOverview>('/v1/free-resources/overview')
            .then(response=>{if(mounted.current&&ticket===latest.current)setOverview(response.data);})
            .catch(()=>{/* keep the last good overview */});
    },[]);
    useEffect(()=>{
        mounted.current=true;
        reload();
        window.addEventListener('registration-account-changed',reload);
        window.addEventListener('focus',reload);
        return ()=>{
            mounted.current=false;
            window.removeEventListener('registration-account-changed',reload);
            window.removeEventListener('focus',reload);
        };
    },[reload]);
    return {overview,reload};
}
